import { facilityCareOps, facilityFrailty } from '@aithora/shared';
import { sx } from '../../ui/designStyles';
import { usePerfApi } from './lib';

// Care operations: the frailty / CHESS risk panel and medication adherence, restored from
// the Flutter production Scorecard (scorecard_view.dart: Care operations, the CHESS / RISK
// panel and the drivers panel). Not in the V3 design, so it is one compact block at the
// foot of Summary, for the building on screen. Both figures are gated by the server's
// reporting floor: below it the block says "insufficient n", never a number.

const BLOCK = 'flex:0 0 auto;display:flex;flex-direction:column;gap:8px;padding-top:10px;border-top:1px solid rgba(34,39,46,.12);min-width:0;';
const HEAD = 'font-size:14px;font-weight:700;letter-spacing:.11em;text-transform:uppercase;color:#54606C;';
const NOTE = 'font-size:14px;color:#54606C;line-height:1.45;text-wrap:pretty;';
const pct = (p: number) => Math.round(p * 1000) / 10 + '%';

function Tile({ k, v, foot, c = '#22272E' }: { k: string; v: string; foot: string; c?: string }) {
  return (
    <div style={sx('flex:1 1 120px;min-width:0;padding:4px 12px 4px 0;')}>
      <div style={sx('font-size:14px;color:#54606C;white-space:nowrap;')}>{k}</div>
      <div style={sx('font-size:19px;font-weight:800;letter-spacing:-.02em;font-variant-numeric:tabular-nums;color:' + c + ';')}>{v}</div>
      <div style={sx('font-size:14px;color:#54606C;')}>{foot}</div>
    </div>
  );
}

/** org: the building (Aithora names one; a Facility Admin's is pinned by the server). */
export function CareOpsRisk({ org, enabled }: { org: string | undefined; enabled: boolean }) {
  const risk = usePerfApi((c, signal) => facilityFrailty(c, { query: { org }, signal }), [org], enabled);
  const ops = usePerfApi((c, signal) => facilityCareOps(c, { query: { org }, signal }), [org], enabled);
  const r = risk.data;
  const m = ops.data?.careOps.medAdherence ?? null;
  return (
    <section aria-label="Care operations" data-testid="perf-careops" style={sx(BLOCK)}>
      <span style={sx(HEAD)}>Care operations · frailty and medicines</span>
      <div style={sx('display:flex;flex-wrap:wrap;gap:4px 0;min-width:0;')}>
        {!r && <span style={sx(NOTE)}>{risk.error ? 'Frailty risk is not available for this building.' : 'Loading frailty risk.'}</span>}
        {r && !r.sufficient && (
          <span style={sx(NOTE)}>{`Frailty risk: insufficient n. ${r.residents} resident${r.residents === 1 ? '' : 's'} with a baseline MDS, below the reporting floor.`}</span>
        )}
        {r && r.sufficient && (
          <>
            <Tile k="Mean CHESS" v={r.chess.mean.toFixed(2)} foot="0 to 5 scale" c={r.chess.mean >= 3 ? '#B3261E' : r.chess.mean >= 1.5 ? '#8A5A00' : '#1F6B52'} />
            <Tile k="CHESS 4 or more" v={String(r.chess.highRisk)} foot={'of ' + r.residents} c={r.chess.highRisk ? '#B3261E' : '#22272E'} />
            <Tile k="MRS3 high risk" v={String(r.mrs3.highRisk)} foot={Math.round(r.mrs3.highRiskPct) + '%'} c={r.mrs3.highRisk ? '#B3261E' : '#22272E'} />
            <Tile k="Mean MRS3" v={r.mrs3.meanScore.toFixed(1)} foot={r.mrs3.meanItemsProvided.toFixed(1) + ' of 17 items on file'} />
          </>
        )}
      </div>
      {r && r.sufficient && (
        <div aria-label="CHESS distribution" style={sx('display:flex;height:10px;border-radius:5px;overflow:hidden;min-width:0;background:rgba(34,39,46,.06);')}>
          {r.chess.distribution.map((n, i) => (
            <span key={i} title={`CHESS ${i} (${r.chess.bands[i] ?? ''}): ${n}`} style={sx('display:block;height:100%;flex:' + n + ' 0 auto;background:' + ['#1F6B52', '#2F8A6F', '#5B6B7C', '#8A5A00', '#B3261E', '#8C3A33'][i] + ';')} />
          ))}
        </div>
      )}
      <div style={sx('display:flex;flex-wrap:wrap;gap:4px 0;min-width:0;')}>
        {!m && <span style={sx(NOTE)}>{ops.error ? 'Medication adherence is not available for this building.' : 'Loading medication adherence.'}</span>}
        {m && !m.sufficient && <span style={sx(NOTE)}>Medication adherence: no administrations recorded in the last 90 days.</span>}
        {m && m.sufficient && (
          <>
            {/* Of every dose due (one per medicine a day, the med pass's unit), not only the
                recorded ones; today's unrecorded doses are still to give, not missed. */}
            <Tile k="Doses given" v={pct(m.givenPct.point)} foot={m.given + ' of ' + m.total + (m.stillToGiveToday ? ' · ' + m.stillToGiveToday + ' still to give today' : '')} c="#1F6B52" />
            <Tile k="Refused" v={pct(m.refusedPct.point)} foot={m.refused + ' (a resident right)'} />
            <Tile k="Held" v={pct(m.heldPct.point)} foot={m.held + ' (a clinical decision)'} />
            <Tile k="Missed" v={pct(m.missedPct.point)} foot={m.undocumented != null && m.undocumented !== m.missed ? m.missed + ' (' + m.undocumented + ' undocumented)' : m.missed + ' undocumented'} c={m.missed ? '#B3261E' : '#22272E'} />
          </>
        )}
      </div>
      <span style={sx(NOTE)}>CHESS (0 to 5) and MRS3 are scored from each resident's baseline MDS. Doses are the last 90 days, one due per medicine a day; a day that ended with nothing recorded is an undocumented miss, the only true error.</span>
    </section>
  );
}
